import { FALLBACK_TIMEZONES } from '@src/constants';
import { mimeTypeMap } from '@src/constants/general.constants';
import { format } from 'date-fns';

type DebounceFn = (...args: any[]) => void;

export const debounce = <T extends DebounceFn>(func: T, delay: number): T & { cancel: () => void } => {
  let timeoutId: NodeJS.Timeout | null = null;

  const debounced = function (...args: any[]) {
    if (timeoutId) clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced as T & { cancel: () => void };
};

export const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// Function to check if `sortBy` is a valid key of IVideoStream
export const isValidKey = <T extends object>(obj: T, key: any): key is keyof T => {
  return key in obj;
};

// Function to dynamically initialize the config using enums
export const generateConfigFromEnum = <T extends string>(
  enumObj: Record<string, T>,
  defaultValue = true
): Record<T, boolean> =>
  Object.fromEntries(Object.values(enumObj).map((key) => [key, defaultValue])) as Record<T, boolean>;

export const getDimensions = (isMobile: boolean = false) => {
  const isPortrait = window.matchMedia('(orientation: portrait)').matches;
  let dimensions;
  if (isMobile) {
    dimensions = isPortrait ? { width: 480, height: 640, isPortrait } : { width: 640, height: 480, isPortrait };
  } else {
    dimensions = { width: 1280, height: 720, isPortrait };
  }
  return dimensions;
};

export const formatDate = (isoDateStr: string): string => {
  return format(new Date(isoDateStr), "MMMM d, yyyy 'at' h:mm a");
};

export const getDataTimeString = () => {
  const d = new Date();
  const date = d.toISOString().split('T')[0];
  const time = d.toTimeString().split(' ')[0];
  return `${date}-${time}`;
};

const getGmtOffset = (date: Date, timeZone: string): string => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));

  const offsetMs = tzDate.getTime() - utcDate.getTime();
  const offsetHours = offsetMs / (1000 * 60 * 60); // convert to hours

  return `GMT${offsetHours >= 0 ? '+' : ''}${offsetHours}`;
};

export const formatDateWithGmt = (isoDateStr?: string | null): string => {
  if (!isoDateStr) return 'N/A';

  const date = new Date(isoDateStr);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  const gmtOffset = getGmtOffset(date, timeZone);

  return `${formattedDate} ${gmtOffset}`;
};

export const preventFormKeys = (e: React.KeyboardEvent<HTMLFormElement>, keys: string[]) => {
  if (keys.includes(e.key)) {
    e.preventDefault();
  }
};

export const getTimezonesWithOffset = () => {
  const timezones: string[] = (Intl as any).supportedValuesOf?.('timeZone') || FALLBACK_TIMEZONES;
  const now = new Date();

  const timezoneList = timezones.map((tz: string) => {
    const offset = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
    })
      .formatToParts(now)
      .find((part) => part.type === 'timeZoneName')?.value;
    const offsetStr = offset?.replace(/^GMT/, 'UTC') ?? 'UTC';
    const offsetFormattedStr = offset?.replace(/^GMT/, 'GMT ') ?? 'GMT ';
    return {
      label: `(${offsetStr}) ${tz}`,
      offsetValue: offsetFormattedStr,
      value: tz,
    };
  });

  return timezoneList;
};

export const checkObjectsEqual = (obj1: any, obj2: any) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      console.log(`Mismatch at key: ${key} -> ${obj1[key]} !== ${obj2[key]}`);
      return false;
    }
  }

  return true;
};

export function getMimeType(extension: string): string {
  const cleanExt = extension.toLowerCase().replace(/^\./, '');
  return mimeTypeMap[cleanExt] || `application/octet-stream`;
}

/**
 * Downloads a file from a given URL
 * @param fileUrl - The URL of the file to download
 * @param filename - Optional custom filename. If not provided, extracts from URL
 */
export const downloadFileFromUrl = async (fileUrl: string, filename?: string): Promise<void> => {
  // Fetch the file from the URL
  const fileResponse = await fetch(fileUrl);
  const blob = await fileResponse.blob();

  // Create a temporary URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Extract filename from the URL if not provided
  const finalFilename = filename || fileUrl.split('/').pop() || 'download';

  // Create a temporary anchor element and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = finalFilename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Gets the current window location URL with only the specified query parameters preserved
 * @param allowedParams - Array of query parameter names to keep in the URL
 * @returns The URL string with only the allowed query parameters, or empty string if window is undefined
 */
export const getUrlWithQueryParams = (allowedParams: string[]): string => {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.href);
  const currentParams = new URLSearchParams(url.search);
  const filteredParams = new URLSearchParams();

  // Only add allowed params to the filtered params
  allowedParams.forEach((param) => {
    const value = currentParams.get(param);
    if (value !== null) {
      filteredParams.set(param, value);
    }
  });

  // Update the URL with filtered params
  url.search = filteredParams.toString();

  return url.toString();
};